import {
    createSelector,
} from 'reselect';
import uuidv4 from 'uuid/v4';

import {
    fireStructuredSnowplowEvent,
} from 'wanderu-service-analytics';

import {
    selectTestVariant,
} from './appstate/selectors';

import type { Cookies } from 'react-cookie';

const SPLIT_TEST_COOKIE_NAME = 'split-test-id';

/**
 * wrap a selector in this function to apply split tests to the data it returns
 * @param {Function} selectorFn - an app state selector to wrap
 * @param {String} testName - name of splitTest as defined in wboard
 * @param {Function} applyTest - a postprocessor function which modifies the original return value of your selector (if the current user is not in the control bucket).  receives app state, additional-selector state, and variant id
 * @param {Function} additionalDataSelector - an app state selector for additional input to the `applyTest` function
 * @return {Function} a wrapped selector
 */
function withSplitTest(selectorFn, testName, applyTest, additionalDataSelector) {
    const otherSelector = additionalDataSelector || (() => undefined);

    return createSelector(
        selectorFn,
        otherSelector,
        (appState) => selectTestVariant(appState, testName),
        (selectedAppData, otherData, variantName) => {
            if (!variantName || variantName === 'control' || variantName === -1) {
                return selectedAppData;
            }

            return applyTest(selectedAppData, otherData, variantName);
        },
    );
}

function fireSplitTestEvent(eventData) {
    if (!eventData || !eventData.property || !eventData.property.treatments
        || !eventData.property.treatments.length) {
        return;
    }

    fireStructuredSnowplowEvent(eventData);

    if (eventData?.action === 'rendered') {
        const [name, variant] = eventData.label.split(':');
        window?.DD_RUM?.addFeatureFlagEvaluation(name, variant);
    }
}

const COOKIE_OPTIONS = {
    path: '/',
    sameSite: true,
    secure: true,
};

function initializeVisitorId(cookies: Cookies) {
    let visitorId = cookies.get(SPLIT_TEST_COOKIE_NAME);

    if (!visitorId) {
        visitorId = uuidv4();

        const domain = typeof document !== 'undefined'
            ? document.location.hostname
            : undefined; // server.  browser will do the right thing

        cookies.set(SPLIT_TEST_COOKIE_NAME, visitorId, {
            ...COOKIE_OPTIONS,
            domain,
            maxAge: 86400 * 365 * 10 * 1000,
        });
    }

    return visitorId;
}

export {
    SPLIT_TEST_COOKIE_NAME,
    fireSplitTestEvent,
    initializeVisitorId,
    withSplitTest,
};
